import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import {
    SendOutlined,
    MailOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    GithubOutlined,
    LinkedinOutlined,
    TwitterOutlined,
    InstagramOutlined,
    UploadOutlined,
    LoadingOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    FileWordOutlined,
    FileImageOutlined,
    FileZipOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { Form, Input, Button, Upload, message, Modal, Tooltip } from 'antd';
import contactService from '../../services/contactService';
import styles from './Contact.module.scss';

const { TextArea } = Input;
const cx = classNames.bind(styles);

function Contact() {
    const [form] = Form.useForm();
    const [isVisible, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    useEffect(() => {
        // Kích hoạt animation sau khi component mount
        setIsVisible(true);

        // Kích hoạt animation khi tab được click
        const animateOnTabClick = () => {
            setIsVisible(false);
            setTimeout(() => setIsVisible(true), 50);
        };

        // Theo dõi khi tab được click lại
        const tabElement = document.querySelector('[href="/lien-he"]');
        if (tabElement) {
            tabElement.addEventListener('click', animateOnTabClick);
        }

        return () => {
            if (tabElement) {
                tabElement.removeEventListener('click', animateOnTabClick);
            }
        };
    }, []);

    const sendContactForm = async (values) => {
        setLoading(true);

        try {
            // Gửi tin nhắn thông qua service
            let file = null;
            if (fileList.length > 0) {
                file = fileList[0].originFileObj;
            }

            // Gọi service để gửi tin nhắn và file
            const result = await contactService.sendContactWithFile(values, file);

            if (result.success) {
                message.success('Gửi liên hệ thành công! Tôi sẽ phản hồi sớm nhất có thể.');
                form.resetFields();
                setFileList([]);
            } else {
                message.error(result.message || 'Có lỗi xảy ra, vui lòng thử lại sau!');
            }
        } catch (error) {
            console.error('Lỗi khi gửi tin nhắn:', error);
            message.error('Có lỗi xảy ra, vui lòng thử lại sau!');
        } finally {
            setLoading(false);
        }
    };

    const onFinish = (values) => {
        sendContactForm(values);
    };

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }

        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
    };

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const uploadButton = (
        <div>
            <UploadOutlined />
            <div style={{ marginTop: 8 }}>Tải lên</div>
        </div>
    );

    const handleCancel = () => setPreviewOpen(false);

    const getBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    // Cấu hình upload
    const uploadProps = {
        onPreview: handlePreview,
        onChange: handleChange,
        fileList: fileList,
        beforeUpload: (file) => {
            // Kiểm tra loại file và kích thước
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.oasis.opendocument.spreadsheet',
                'application/vnd.oasis.opendocument.text',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'image/jpeg',
                'image/png',
                'image/gif',
                'image/webp',
                'application/zip',
                'application/x-zip-compressed',
                'application/x-rar-compressed',
                'application/x-7z-compressed',
                'text/plain',
                'text/csv',
            ];

            const isValidFormat = allowedTypes.includes(file.type);
            const isLt10M = file.size / 1024 / 1024 < 10;

            if (!isValidFormat) {
                message.error('Định dạng file không được hỗ trợ!');
            }

            if (!isLt10M) {
                message.error('File phải nhỏ hơn 10MB!');
            }

            // Không tải lên ngay lập tức, chỉ thêm vào fileList
            return isValidFormat && isLt10M ? false : Upload.LIST_IGNORE;
        },
        maxCount: 1,
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
        iconRender: (file) => {
            const fileType = file.type;
            // Chọn icon phù hợp dựa trên loại file
            if (fileType.includes('pdf')) {
                return <FilePdfOutlined style={{ color: '#f5222d', fontSize: '24px' }} />;
            } else if (fileType.includes('excel') || fileType.includes('spreadsheet') || fileType.includes('csv')) {
                return <FileExcelOutlined style={{ color: '#52c41a', fontSize: '24px' }} />;
            } else if (fileType.includes('word') || fileType.includes('opendocument.text')) {
                return <FileWordOutlined style={{ color: '#1890ff', fontSize: '24px' }} />;
            } else if (fileType.includes('image')) {
                return <FileImageOutlined style={{ color: '#722ed1', fontSize: '24px' }} />;
            } else if (fileType.includes('zip') || fileType.includes('compressed')) {
                return <FileZipOutlined style={{ color: '#faad14', fontSize: '24px' }} />;
            } else {
                return <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '24px' }} />;
            }
        },
    };

    return (
        <div className={cx('contact-container')}>
            {/* Background trang trí */}
            <div className={cx('contact-background')}>
                <div className={cx('bg-shape', 'shape-1')}></div>
                <div className={cx('bg-shape', 'shape-2')}></div>
                <div className={cx('bg-shape', 'shape-3')}></div>
                <div className={cx('bg-shape', 'shape-4')}></div>
                <div className={cx('bg-shape', 'shape-5')}></div>
                <div className={cx('bg-shape', 'shape-6')}></div>
            </div>

            <div className={cx('contact-content', { 'animate-content': isVisible })}>
                <div className={cx('contact-title-section')}>
                    <h1 className={cx('contact-title')}>Liên hệ với tôi</h1>
                    <p className={cx('contact-subtitle')}>
                        Hãy để lại thông tin liên hệ, tôi sẽ phản hồi trong thời gian sớm nhất!
                    </p>
                    <p className={cx('contact-name')}>Nguyễn Mạnh Phương - Fullstack Developer</p>
                </div>

                <div className={cx('contact-main')}>
                    <div className={cx('contact-info-section')}>
                        <div className={cx('info-item')}>
                            <div className={cx('info-icon')}>
                                <MailOutlined />
                            </div>
                            <div className={cx('info-content')}>
                                <h3>Email</h3>
                                <p>manhphuong.dev@gmail.com</p>
                            </div>
                        </div>

                        <div className={cx('info-item')}>
                            <div className={cx('info-icon')}>
                                <PhoneOutlined />
                            </div>
                            <div className={cx('info-content')}>
                                <h3>Điện thoại</h3>
                                <p>+84 </p>
                            </div>
                        </div>

                        <div className={cx('info-item')}>
                            <div className={cx('info-icon')}>
                                <EnvironmentOutlined />
                            </div>
                            <div className={cx('info-content')}>
                                <h3>Địa chỉ</h3>
                                <p>Hà Nội, Việt Nam</p>
                            </div>
                        </div>

                        <div className={cx('social-links')}>
                            <Tooltip title="Twitter">
                                <div className={cx('social-link')}>
                                    <TwitterOutlined />
                                </div>
                            </Tooltip>
                            <Tooltip title="GitHub">
                                <a
                                    href="https://github.com/Manh-Phuong"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cx('social-link')}
                                >
                                    <GithubOutlined />
                                </a>
                            </Tooltip>
                            <Tooltip title="LinkedIn">
                                <a
                                    href="https://www.linkedin.com/in/manh-phuong/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cx('social-link')}
                                >
                                    <LinkedinOutlined />
                                </a>
                            </Tooltip>
                            <Tooltip title="Instagram">
                                <div className={cx('social-link')}>
                                    <InstagramOutlined />
                                </div>
                            </Tooltip>
                            <Tooltip title="Email">
                                <a href="mailto:manhphuong.dev@gmail.com" className={cx('social-link')}>
                                    <MailOutlined />
                                </a>
                            </Tooltip>
                        </div>
                    </div>

                    <div className={cx('contact-form-section')}>
                        <Form form={form} layout="vertical" onFinish={onFinish} className={cx('contact-form')}>
                            <Form.Item
                                name="name"
                                label="Họ và tên"
                                rules={[{ required: true, message: 'Vui lòng nhập họ và tên!' }]}
                            >
                                <Input placeholder="Nhập họ và tên của bạn" />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập email!' },
                                    { type: 'email', message: 'Email không hợp lệ!' },
                                ]}
                            >
                                <Input placeholder="Nhập email của bạn" />
                            </Form.Item>

                            <Form.Item
                                name="phone"
                                label="Số điện thoại"
                                rules={[{ pattern: /^[0-9\+\-\s]+$/, message: 'Số điện thoại không hợp lệ!' }]}
                            >
                                <Input placeholder="Nhập số điện thoại của bạn (không bắt buộc)" />
                            </Form.Item>

                            <Form.Item
                                name="subject"
                                label="Tiêu đề"
                                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề!' }]}
                            >
                                <Input placeholder="Nhập tiêu đề liên hệ" />
                            </Form.Item>

                            <Form.Item
                                name="message"
                                label="Nội dung"
                                rules={[{ required: true, message: 'Vui lòng nhập nội dung liên hệ!' }]}
                            >
                                <TextArea rows={5} placeholder="Nhập nội dung liên hệ chi tiết" />
                            </Form.Item>

                            <Form.Item
                                name="attachment"
                                label="Đính kèm tệp (nếu có)"
                                extra="Hỗ trợ: PDF, Word, Excel, PowerPoint, Image, Zip, CSV. Kích thước tối đa: 10MB"
                            >
                                <Upload listType="picture-card" {...uploadProps}>
                                    {fileList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className={cx('submit-button')}
                                    icon={loading ? <LoadingOutlined /> : <SendOutlined />}
                                    loading={loading}
                                >
                                    Gửi liên hệ
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </div>

            <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
                <img alt="preview" style={{ width: '100%' }} src={previewImage} />
            </Modal>
        </div>
    );
}

export default Contact;
